
import React, { useState } from 'react';
import { InventoryItem } from '../types';
import { Plus, Trash2, Save, X, List, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const AFFECTS_OPTIONS = [
  'Self-esteem',
  'Security',
  'Ambitions',
  'Personal Relations',
  'Sex Relations',
  'Pocketbook (Finances)',
  'Pride'
];

const MY_PART_OPTIONS = [
  'Selfish',
  'Dishonest',
  'Self-seeking',
  'Frightened',
  'Inconsiderate'
];

interface InventoryPageProps {
  inventory: InventoryItem[];
  onAdd: (item: Omit<InventoryItem, 'id'>) => void;
  onDelete: (id: string) => void;
}

export const InventoryPage: React.FC<InventoryPageProps> = ({ inventory, onAdd, onDelete }) => {
  const [isAdding, setIsAdding] = useState(false);

  // Form State
  const [resentment, setResentment] = useState('');
  const [cause, setCause] = useState('');
  const [selectedAffects, setSelectedAffects] = useState<string[]>([]);
  const [selectedMyPart, setSelectedMyPart] = useState<string[]>([]);

  const handleToggleOption = (
    option: string, 
    current: string[], 
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (current.includes(option)) {
      setter(current.filter(i => i !== option));
    } else {
      setter([...current, option]);
    }
  };

  const handleSave = () => {
    if (!resentment || !cause) return;
    
    onAdd({
      resentment,
      cause,
      affects: selectedAffects,
      myPart: selectedMyPart
    });

    resetForm();
    setIsAdding(false);
  };

  const resetForm = () => {
    setResentment('');
    setCause('');
    setSelectedAffects([]);
    setSelectedMyPart([]);
  };

  return (
    <div className="space-y-6">
      {/* HEADER WITH BACK BUTTON */}
      <div className="flex flex-col gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-brand-600 transition-colors text-sm font-medium">
             <ArrowLeft className="w-4 h-4" /> Back to Hub
          </Link>
          <div className="flex justify-between items-center">
            <div>
            <h2 className="text-2xl font-bold dark:text-white flex items-center gap-2">
                <List className="w-6 h-6 text-brand-500" /> Step 4 Inventory
            </h2>
            <p className="text-sm text-slate-500">"We made a searching and fearless moral inventory of ourselves."</p>
            </div>
            {!isAdding && (
            <button 
                onClick={() => setIsAdding(true)}
                className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 shadow-sm"
            >
                <Plus className="w-4 h-4" /> Add Entry
            </button>
            )}
        </div>
      </div>

      {isAdding && (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700 animate-in slide-in-from-top-4 duration-300">
          <div className="flex justify-between items-start mb-6">
             <h3 className="font-bold text-lg dark:text-white">New Resentment</h3>
             <button onClick={() => setIsAdding(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white">
               <X className="w-5 h-5" />
             </button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="col-span-2 md:col-span-1">
               <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Who or What?</label>
               <input 
                 className="w-full p-3 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                 placeholder="e.g. My Boss, The Government, My Brother"
                 value={resentment}
                 onChange={e => setResentment(e.target.value)}
                 autoFocus
               />
            </div>
            <div className="col-span-2 md:col-span-1">
               <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">The Cause</label>
               <input 
                 className="w-full p-3 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                 placeholder="e.g. He threatened my job security."
                 value={cause}
                 onChange={e => setCause(e.target.value)}
               />
            </div>
            
            <div className="col-span-2 md:col-span-1">
               <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Affects My...</label>
               <div className="flex flex-wrap gap-2">
                 {AFFECTS_OPTIONS.map(opt => (
                   <button
                     key={opt}
                     onClick={() => handleToggleOption(opt, selectedAffects, setSelectedAffects)}
                     className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                       selectedAffects.includes(opt) 
                         ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 shadow-sm' 
                         : 'bg-white border-slate-200 text-slate-600 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400 hover:border-slate-400'
                     }`}
                   >
                     {opt}
                   </button>
                 ))}
               </div>
            </div>

            <div className="col-span-2 md:col-span-1">
               <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">My Part (Character Defects)</label>
               <div className="flex flex-wrap gap-2">
                 {MY_PART_OPTIONS.map(opt => (
                   <button
                     key={opt}
                     onClick={() => handleToggleOption(opt, selectedMyPart, setSelectedMyPart)}
                     className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                       selectedMyPart.includes(opt) 
                         ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-800 dark:text-indigo-300 shadow-sm' 
                         : 'bg-white border-slate-200 text-slate-600 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400 hover:border-slate-400'
                     }`}
                   >
                     {opt}
                   </button>
                 ))}
               </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
             <button 
               onClick={() => setIsAdding(false)}
               className="px-4 py-2 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
             >
               Cancel
             </button>
             <button 
               onClick={handleSave}
               disabled={!resentment || !cause}
               className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               <Save className="w-4 h-4" /> Save Entry
             </button>
          </div>
        </div>
      )}

      {/* Inventory Table */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden min-h-[400px]">
        {inventory.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
             <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-full mb-3">
                <List className="w-8 h-8" />
             </div>
             <p>Your inventory is currently empty.</p>
             <button onClick={() => setIsAdding(true)} className="text-brand-600 font-medium hover:underline mt-2">Start writing</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  <th className="px-6 py-4 font-bold w-1/4">Resentment</th>
                  <th className="px-6 py-4 font-bold w-1/4">The Cause</th>
                  <th className="px-6 py-4 font-bold w-1/6">Affects My...</th>
                  <th className="px-6 py-4 font-bold w-1/6">My Part</th>
                  <th className="px-6 py-4 font-bold w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {inventory.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4 align-top">
                      <div className="font-medium text-slate-900 dark:text-white">{item.resentment}</div>
                    </td>
                    <td className="px-6 py-4 align-top text-slate-600 dark:text-slate-300 text-sm">
                      {item.cause}
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-wrap gap-1">
                        {item.affects.map(a => (
                          <span key={a} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 border border-red-100 dark:border-red-900/50">
                            {a}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-wrap gap-1">
                        {item.myPart.map(p => (
                          <span key={p} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50">
                            {p}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top text-right">
                      <button 
                        onClick={() => onDelete(item.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
